import './tabparcel.css';
import React, { useState, useMemo } from 'react';
import { Container, Table, Button, Dropdown, Modal, ProgressBar } from 'react-bootstrap';
import {
  BsPlus,
  BsPencil,
  BsChevronDown,
  BsChevronRight,
  BsFilter,
  BsPlayFill,
  BsPauseFill,
  BsStopFill,
  BsEyeFill,
  BsBarChartFill
} from 'react-icons/bs';
import appConfig from '../../config/config';
import ParcelForm from '../ParcelForm/ParcelForm';
import OperationCreator from '../OperationCreator/OperationCreator';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/ToastContext/ToastContext';
import { ServiceFactory } from '../../Api/Services/ServiceFactory';
import { ServiceNames } from '../../Api/registry/Services';
import { fetchPerson } from '../../store/slices/personSlice';

const TabParcel = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const person = useSelector(state => state.person.person);

  // Ricostruisci le parcels complete
  const parcels = useMemo(() => {
    if (!person?.parcels) { return []; }

    return person.parcels.map(parcel => {
      // Se la parcel ha già il name, è completa
      // Altrimenti cerca la parcel completa nelle farms
      const parcelId = typeof parcel === 'string' ? parcel : parcel.id;

      // Cerca in tutte le farms
      for (const farm of (person.agriFarms || [])) {

        const foundParcel = farm.parcels?.find(p => p === parcelId);
        if (foundParcel) {
          return {
            ...parcel,
            farmReference: {
              id: farm.id,
              name: farm.name
            }
          };
        }
      }
      // Se non trovata, ritorna la parcel originale
      return parcel;
    });
  }, [person]);


  // Costruisci una mappa cropReferenceId -> cropReference completo
  const cropReferenceMap = useMemo(() => {
    const map = new Map();
    function addOperationsFromParcels(parcelList) {
      for (const parcel of parcelList || []) {
        for (const op of parcel.parcelOperations || []) {
          if (op.cropReference && op.cropReference.id && op.cropReference.name) {
            map.set(op.cropReference.id, op.cropReference);
          }
        }
      }
    }
    // Da tutte le farm
    for (const farm of person?.agriFarms || []) {
      addOperationsFromParcels(farm.parcels);
    }
    // Dalla lista parcels diretta
    addOperationsFromParcels(person?.parcels);
    return map;
  }, [person]);


  const [selectedParcel, setSelectedParcel] = useState(null);
  const [showParcelForm, setShowParcelForm] = useState(false);
  const [showOperationCreator, setShowOperationCreator] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [formMode, setFormMode] = useState('create');
  const [farmNameFilter, setFarmNameFilter] = useState('');
  const [showOperationDetail, setShowOperationDetail] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [showStopModal, setShowStopModal] = useState(false);
  const [activateStop, setActivateToStop] = useState(null);
  const loading = useSelector(state => state.person.loading);

  // Get unique farm names for filter from parcels
  const uniqueFarmNames = useMemo(() => {
    const farmNames = parcels.map(parcel => parcel.farmReference?.name || null);
    return [...new Set(farmNames)];
  }, [parcels]);

  // Filter parcels based on selected farm name
  const filteredParcels = useMemo(() => {
    const filtered = !farmNameFilter ? parcels
      : farmNameFilter === 'No Farm'
        ? parcels.filter(parcel => !parcel?.farmReference?.name)
        : parcels.filter(parcel => parcel?.farmReference?.name === farmNameFilter);
    return filtered;
  }, [farmNameFilter, parcels]);

  const getParcelStatus = (parcel) => {
    if (!parcel?.parcelOperations) return 'Not Monitored';
    const hasStartedOperation = parcel.parcelOperations.some(op => op.status === "Started");
    return hasStartedOperation ? 'Under Monitoring' : 'Not Monitored';
  };

  const handleViewUpdate = (parcel) => {
    setFormMode('update');
    setSelectedParcel(parcel);
    setShowParcelForm(true);
  };

  const toggleRow = (parcelId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(parcelId)) {
      newExpandedRows.delete(parcelId);
    } else {
      newExpandedRows.add(parcelId);
    }
    setExpandedRows(newExpandedRows);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleAddOperation = (parcelId) => {
    const parcel = parcels.find(p => p.id === parcelId);
    if (parcel) {
      setSelectedParcel(parcel);
      setShowOperationCreator(true);
    }
  };

  const handleViewOperation = (operation) => {
    setSelectedOperation(operation);
    setShowOperationDetail(true);
  };

  const handleOpenDashboard = (parcel, operation) => {
    let croRefId;
    if (operation.cropReference?.id) {
      croRefId = operation.cropReference.id;
    } else {
      croRefId = operation.cropReference;
    }

    const queryParams = new URLSearchParams({
      parcelId: parcel.id,
      operationId: operation.id
    }).toString();

    const baseUrl = appConfig.DASHBOARD_URL;
    let fullUrl;
    if (queryParams) {
      if (baseUrl.includes('?')) {
        fullUrl = `${baseUrl}&${queryParams}`;
      } else {
        fullUrl = `${baseUrl}?${queryParams}`;
      }
    } else {
      fullUrl = baseUrl;
    }

    window.location.href = fullUrl;
  };

  const refreshPersonData = async () => {
    try {
      await dispatch(fetchPerson());
    } catch (error) {
      console.error('Error refreshing person data:', error);
      showToast(t("error:wrong"), 'error');
    }
  };

  const handleStartOperation = async (operation) => {
    if (!['Not yet started', 'Paused'].includes(operation.status)) {
      return;
    }
    try {
      const userService = await ServiceFactory.getService(ServiceNames.UserService);
      await userService.updateParcelOperationStatus(operation.id, {
        parcelOperationId: operation.id,
        requestedOperation: 'Start'
      });
      showToast(t("common:messages.success"), "success");
      await refreshPersonData();
    } catch (error) {
      showToast(error.message || t("error:wrong"), "error");
    }
  };

  const handlePauseOperation = async (operation) => {
    if (operation.status !== 'Started') {
      return;
    }
    try {
      const userService = await ServiceFactory.getService(ServiceNames.UserService);
      await userService.updateParcelOperationStatus(operation.id, {
        parcelOperationId: operation.id,
        requestedOperation: 'Pause'
      });
      showToast(t("common:messages.success"), "success");
      await refreshPersonData();
    } catch (error) {
      showToast(error.message || t("error:wrong"), "error");
    }
  };

  const handleStopOperation = async (operation) => {
    if (operation.status !== 'Started') {
      return;
    }
    try {
      const userService = await ServiceFactory.getService(ServiceNames.UserService);
      await userService.updateParcelOperationStatus(operation.id, {
        parcelOperationId: operation.id,
        requestedOperation: 'Stop'
      });
      showToast(t("common:messages.success"), "success");
      await refreshPersonData();
    } catch (error) {
      showToast(error.message || t("error:wrong"), "error");
    }
  };

  const renderOperationActions = (parcel, operation) => {
    const isNotYetStarted = operation.status === 'Not yet started';
    const isStarted = operation.status === 'Started';
    const isPaused = operation.status === 'Paused';

    return (
      <div className="action-buttons">
        {/* View Details - sempre disponibile */}
        <button
          className="action-button"
          onClick={() => handleViewOperation(operation)}
          title={t("tabparcel:buttons.viewOperationDetails")}
        >
          <BsEyeFill size={18} />
        </button>

        {/* Dashboard Link - sempre disponibile */}
        <button
          className="action-button"
          onClick={() => handleOpenDashboard(parcel, operation)}
          title={t("tabparcel:buttons.openDashboard")}
        >
          <BsBarChartFill size={18} />
        </button>

        {/* Start/Resume - disponibile se Not yet started o Paused */}
        {(isNotYetStarted || isPaused) && (
          <button
            className="action-button text-success"
            onClick={() => handleStartOperation(operation)}
            title={isPaused ? t("tabparcel:buttons.resumeOperation") : t("tabparcel:buttons.startOperation")}
          >
            <BsPlayFill size={18} />
          </button>
        )}

        {/* Pause - disponibile se Started */}
        {isStarted && (
          <button
            className="action-button text-warning"
            onClick={() => handlePauseOperation(operation)}
            title={t("tabparcel:buttons.pauseOperation")}
          >
            <BsPauseFill size={18} />
          </button>
        )}

        {/* Stop - disponibile se Started */}
        {isStarted && (
          <button
            className="action-button text-danger"
            onClick={() => {
              setActivateToStop(operation);
              setShowStopModal(true);
            }}
            title={t("tabparcel:buttons.stopOperation")}
          >
            <BsStopFill size={18} />
          </button>
        )}
      </div>
    );
  };

  const renderWeatherVariables = (setting, operation) => {
    if (!setting.weatherDataSetting) return null;

    const groupedVariables = {
      station: setting.weatherDataSetting
        .filter(ws => ws.stationLatitude !== null && ws.stationLongitude !== null)
        .map(ws => ws.meteoVariable),
    };

    const hasStationData = groupedVariables.station.length > 0;
    const stationCoordinates = hasStationData ? setting.weatherDataSetting.find(ws => ws.stationLatitude !== null) : null;

    return (
      <div className="weather-sources">
        {hasStationData && (
          <div className="weather-source-group">
            <div className="source-header">
              <span><i className="bi bi-broadcast-pin"></i>{" " + t("tabparcel:caption.station")} <br />
                {t("tabparcel:caption.stationName") + ":"} {stationCoordinates.stationName} <br />
                {t("tabparcel:caption.latitude") + ":"} {stationCoordinates.stationLatitude.toFixed(6)} <br />
                {t("tabparcel:caption.longitude") + ":"} {stationCoordinates.stationLongitude.toFixed(6)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const OperationDetailModal = ({ operation, show, onHide, parcel }) => {
    if (!operation) return null;
    const today = new Date();
    const startDate = new Date(operation.plannedStartAt);
    const endDate = new Date(operation.plannedEndAt);
    const totalDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
    const elapsedDays = Math.max(0, Math.min(totalDays, (today - startDate) / (1000 * 60 * 60 * 24)));
    const progress = (elapsedDays / totalDays) * 100;

    return (
      <Modal show={show} onHide={onHide} size="lg" dialogClassName="operation-detail-dialog">
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="operation-title">
              <div className="title-main">
                <div className="title-crop">
                  <i className="bi bi-flower1"></i>
                  <span>{operation.cropReference.name}</span>
                </div>
                <div className="title-parcel">
                  <i className="bi bi-geo-alt"></i>
                  <span>{parcel.name}</span>
                  <span className="coordinates">({parcel.latitude}, {parcel.longitude})</span>
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="timeline-section">
            <div className="progress-container">
              <div className="timeline-dates">
                <span>{startDate.toLocaleDateString()}</span>
                <span className="operation-status">{operation.status}</span>
                <span>{endDate.toLocaleDateString()}</span>
              </div>
              <ProgressBar now={progress} variant="success" />
            </div>
          </div>

          <div className="models-section">
            {operation.settings.map((setting) => {
              let cropRef = null;
              if (!operation.cropReference?.name && cropReferenceMap.has(operation.cropReference)) {
                console.log("#SIM_DEBUG-1 - operation.cropReference:", operation.cropReference);
                cropRef = cropReferenceMap.get(operation.cropReference);

                if (!cropRef) return null;
              } else {
                cropRef = operation.cropReference;
              }
              console.log("#SIM_DEBUG-2 - cropRef:", cropRef);
              
              const modelData = cropRef.models.find(m => m.id === setting.model);
              if (!modelData) return null;

              return (
                <div key={setting.id} className="model-panel">
                  <div className="model-panel-header">
                    <h5>
                      <i className="bi bi-graph-up"></i>
                      {modelData.name}
                    </h5>
                    <div dangerouslySetInnerHTML={{ __html: modelData.description }} />
                  </div>

                  <div className="model-content">
                    <div className="parameters-section">
                      <h6>
                        <i className="bi bi-sliders"></i>
                        {t("tabparcel:labels.parameters")}
                      </h6>
                      <div className="parameters-grid">
                        {Object.entries(setting.parameters || {}).map(([key, value]) => {
                          let displayValue;
                          if (typeof value === 'boolean') {
                            displayValue = <i className={`bi bi-${value ? 'check-circle-fill text-success' : 'x-circle-fill text-danger'}`}></i>;
                          } else if (typeof value === 'object' && value !== null) {
                            displayValue = JSON.stringify(value);
                          } else {
                            displayValue = String(value);
                          }

                          return (
                            <div key={key} className="parameter-card">
                              <div className="parameter-name">{key}</div>
                              <div className="parameter-value">{displayValue}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="weather-section">
                      <h6>
                        <i className="bi bi-cloud-sun"></i>
                        {t("tabparcel:labels.weatherDataSource")}
                      </h6>
                      {renderWeatherVariables(setting, operation)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <Container fluid className="parcel-container">
      {loading || !person ? (
        <div className="loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t("tabparcel:labels.loading")}</span>
          </div>
        </div>
      ) : (
        <>
          <div className="parcel-table-container">
            <div className="parcel-table-scroll">
              <Table className="parcel-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>{t("tabparcel:labels.name")}</th>
                    <th>{t("tabparcel:labels.description")}</th>
                    <th className="farm-name-header">
                      {t("tabparcel:labels.farmName")}
                      <Dropdown className="farm-filter-dropdown">
                        <Dropdown.Toggle variant="link" size="sm" className="farm-filter-button">
                          <BsFilter />
                        </Dropdown.Toggle>
                        <Dropdown.Menu popperConfig={{ strategy: 'absolute' }} rootClose>
                          <Dropdown.Item
                            onClick={() => setFarmNameFilter('')}
                            active={farmNameFilter === ''}
                          >
                            {t("tabparcel:labels.allFarms")}
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => setFarmNameFilter('No Farm')}
                            active={farmNameFilter === 'No Farm'}
                          >
                            {t("tabparcel:labels.noFarms")}
                          </Dropdown.Item>
                          {uniqueFarmNames.map(farmName => (
                            <Dropdown.Item
                              key={farmName}
                              onClick={() => setFarmNameFilter(farmName)}
                              active={farmNameFilter === farmName}
                            >
                              {farmName}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </th>
                    <th>{t("tabparcel:labels.position")}</th>
                    <th>{t("tabparcel:labels.currentStatus")}</th>
                    <th>{t("tabparcel:labels.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParcels.map((parcel) => (
                    <React.Fragment key={parcel.id}>
                      <tr
                        className={`parcel-row ${getParcelStatus(parcel) === 'Under Monitoring' ? 'parcel-row-monitored' : ''} ${expandedRows.has(parcel.id) ? 'expanded' : ''}`}
                        onClick={() => toggleRow(parcel.id)}
                      >
                        <td className="expand-cell">
                          {expandedRows.has(parcel.id) ? <BsChevronDown /> : <BsChevronRight />}
                        </td>
                        <td>{parcel.name}</td>
                        <td>{parcel.description}</td>
                        <td>{parcel.farmReference?.name || 'No Farm'}</td>
                        <td className="position-cell">
                          {typeof parcel?.latitude === 'number' && typeof parcel?.longitude === 'number'
                            ? `${parcel.latitude.toFixed(4)}, ${parcel.longitude.toFixed(4)}`
                            : 'N/A'}
                        </td>
                        <td>{getParcelStatus(parcel)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="action-buttons">
                            <button
                              className="action-button"
                              onClick={() => handleViewUpdate(parcel)}
                              title={t("tabparcel:buttons.viewUpdateParcel")}
                            >
                              <BsPencil size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedRows.has(parcel.id) && (
                        <tr className="operations-row">
                          <td colSpan="7">
                            <div className="operations-container">
                              <div className="operations-header">
                                <h5>{t("tabparcel:labels.operationsFor")} {parcel.name}</h5>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleAddOperation(parcel.id)}
                                >
                                  <BsPlus size={20} /> {t("tabparcel:buttons.addOperation")}
                                </Button>
                              </div>
                              <Table className="operations-table" size="sm">
                                <thead style={{ position: 'sticky' }}>
                                  <tr>
                                    <th>{t("tabparcel:labels.fromDate")}</th>
                                    <th>{t("tabparcel:labels.toDate")}</th>
                                    <th>{t("tabparcel:labels.crop")}</th>
                                    <th>{t("tabparcel:labels.status")}</th>
                                    <th>{t("tabparcel:labels.actions")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {parcel.parcelOperations.map((operation) => {
                                    let cropRef = operation.cropReference;
                                    if (!cropRef?.name && cropReferenceMap.has(cropRef)) {
                                      cropRef = cropReferenceMap.get(cropRef);
                                    } else if (!cropRef?.name) {
                                      console.warn('[TabParcel] CropReference name missing and not found in map for operation', operation.id, 'with cropId', cropRef);
                                    }
                                    return (
                                      <tr key={operation.id}>
                                        <td>{formatDate(operation.plannedStartAt)}</td>
                                        <td>{formatDate(operation.plannedEndAt)}</td>
                                        <td>{cropRef?.name}</td>
                                        <td>{operation.status}</td>
                                        <td>
                                          {renderOperationActions(parcel, operation)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {parcel.parcelOperations.length === 0 && (
                                    <tr>
                                      <td colSpan="5" className="text-center">
                                        {t("tabparcel:messages.noOperationFound")}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

          <div className="parcel-legend">
            <div className="legend-item">
              <div className="legend-color legend-monitored"></div>
              <span>{t("tabparcel:labels.underMonitoring")}</span>
            </div>
            <div className="legend-item">
              <div className="legend-color legend-default"></div>
              <span>{t("tabparcel:labels.notMonitored")}</span>
            </div>
          </div>

          <ParcelForm
            show={showParcelForm}
            onHide={() => setShowParcelForm(false)}
            parcel={selectedParcel}
            mode={formMode}
          />

          <OperationCreator
            show={showOperationCreator}
            onHide={() => setShowOperationCreator(false)}
            parcel={selectedParcel}
          />

          <OperationDetailModal
            show={showOperationDetail}
            onHide={() => setShowOperationDetail(false)}
            operation={selectedOperation}
            parcel={filteredParcels.find(p => p?.parcelOperations?.some(op => op?.id === selectedOperation?.id))}
          />

          <Modal show={showStopModal} onHide={() => setShowStopModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>{t("tabparcel:labels.confirmStopTitle")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{t("tabparcel:messages.stopOperation")}</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowStopModal(false)}>
                {t("tabparcel:buttons.cancel")}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  handleStopOperation(activateStop);
                  setShowStopModal(false);
                }}
              >
                {t("tabparcel:buttons.confirm")}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default TabParcel;